<template>
  <div id="app" class="wrapper">
    <topnavbar 
      v-if="checkRoute() && shouldRoutesBeDisplayed" 
      @force-update="forceUpdate"
      @toggle-drawer="toggleDrawer"
    ></topnavbar>

    <sidebar 
      ref="sidebar" 
      v-if="checkRoute() && !irAdmin && shouldRoutesBeDisplayed"
      :drawer-open="isDrawerOpen"
      @drawer-closed="handleDrawerClosed"
    ></sidebar>

    <iradminsidebar v-if="checkRoute() && irAdmin && shouldRoutesBeDisplayed"> </iradminsidebar>

    <router-view ref="accountSummary" v-if="shouldRoutesBeDisplayed"></router-view>
    <maintenance v-if="maintenance"></maintenance>

    <!-- Phone Preference Dialog - Shows after login if needed -->
    <mfa-phone-preference-dialog
      :visible="showPhonePreferenceDialog"
      :user="currentUser"
      @saved="handlePhonePreferenceSaved"
      @cancel="handlePhonePreferenceCancel"
    />
  </div>
</template>

<script>
import TopNavBar from '@/components/TopNavBar';
import SideBar from '@/components/SideBar';
import AccountSummary from '@/components/AccountSummary';
import IRAdminSideBar from '@/components/IRAdminSideBar';
import Maintenance from '@/components/Maintenance';
import MfaPhonePreferenceDialog from '@/components/MfaPhonePreferenceDialog';
import { getUserIdleTimeConfig } from '@/util';

import {
  mapMutations,
  mapActions,
  mapState,
  mapGetters,
} from 'vuex';
import {
  SET_USER,
  SET_ACCOUNT_STATUS,
  SET_IDLE_ON,
} from '@/store/mutation-types';

export default {
  name: 'app',
  async beforeMount() {
    await this.getStaticInformation();
    const isAuthenticated = await this.$cognitoAuth.isAuthenticated();
    this.shouldRoutesBeDisplayed = !this.maintenance;
    if (isAuthenticated && this.shouldRoutesBeDisplayed) {
      const user = await this.getUserProfile();
      console.log('beforeMount: User profile loaded', {
        userId: user.userId,
        isMfaEnabled: user.isMfaEnabled,
        mfaPhonePreference: user.mfaPhonePreference,
      });
      this.setUser(user);
      this.user = user;
      this.currentUser = user;
      this.checkForAccountStatus(user);

      // Check if user needs to set up phone MFA after login
      this.checkPhoneMfaSetup(user);
    }
  },
  mounted() {
    console.log('mounted: Checking MFA setup', {
      shouldRoutesBeDisplayed: this.shouldRoutesBeDisplayed,
      hasUser: !!this.user,
      userId: this.user.userId,
    });
    // Also check when component mounts (after route navigation)
    if (this.shouldRoutesBeDisplayed && this.user && this.user.userId) {
      this.checkPhoneMfaSetup(this.user);
    }
  },
  updated() {
    if (this.$refs.sidebar && this.hasBeenCreatedAccount) {
      this.$refs.sidebar.checkUserStatus();
    }
  },
  created() {
    this.startIdleTimer();

    if (location.search) {
      const qs = location.search.slice(1);
      let qsObj = qs.split('&').reduce((obj, e) => {
        const [key, value] = e.split('=');
        obj[key] = decodeURIComponent(value); // eslint-disable-line no-param-reassign
        return obj;
      }, {});
      qsObj = Object.assign({}, this.$router.currentRoute.query, qsObj);
      const newUrlHash = this.$router.resolve({
        query: qsObj,
      }).href;
      location.replace(`/${newUrlHash}`);
    }
  },
  components: {
    sidebar: SideBar,
    topnavbar: TopNavBar,
    iradminsidebar: IRAdminSideBar,
    maintenance: Maintenance,
    accountSummary: AccountSummary,
    MfaPhonePreferenceDialog,
  },
  data() {
    return {
      user: {
        roles: [],
      },
      currentRoute: window.location.pathname,
      shouldRoutesBeDisplayed: false,
      showPhonePreferenceDialog: false,
      currentUser: null,
      isDrawerOpen: false,
    };
  },
  computed: {
    ...mapState('app', [
      'irAdmin',
      'maintenance',
    ]),
    ...mapGetters('app', [
      'hasBeenCreatedAccount',
    ]),
    ...mapState('idle', [
      'isUserIdle',
    ]),
  },
  watch: {
    isUserIdle(idle) {
      if (idle) this.onIdle();
    },
    irAdmin() {
      this.startIdleTimer();
    },
    $route: {
      handler: async function handler() {
        // Skip user profile fetching for public routes (Login, ResetPassword, etc.)
        if (!this.checkRoute()) {
          return;
        }

        // Check phone MFA setup after route navigation
        // This ensures the dialog shows after user lands on their page
        console.log('Route changed, checking MFA setup', {
          shouldRoutesBeDisplayed: this.shouldRoutesBeDisplayed,
          hasUser: !!this.user,
          userId: this.user.userId,
        });
        if (this.shouldRoutesBeDisplayed) {
          // Ensure we have fresh user data after route navigation
          // This is especially important after login when App.vue is already mounted
          let userToCheck = this.user;
          if (!userToCheck || !userToCheck.userId) {
            try {
              userToCheck = await this.getUserProfile();
              this.setUser(userToCheck);
              this.user = userToCheck;
              this.currentUser = userToCheck;
            } catch (error) {
              console.error('Error fetching user profile on route change:', error);
              return;
            }
          }

          // Small delay to let the page render first
          this.$nextTick(() => {
            this.checkPhoneMfaSetup(userToCheck);
          });
        }
      },
    },
  },
  methods: {
    ...mapActions('accountCreation', [
      'getUserProfile',
    ]),
    ...mapMutations('app', {
      setUser: SET_USER,
      setAccountStatus: SET_ACCOUNT_STATUS,
    }),
    ...mapMutations('authentication', {
      setIdle: SET_IDLE_ON,
    }),
    ...mapActions('authentication', [
      'logOut',
    ]),
    ...mapActions('app', [
      'getStaticInformation',
    ]),
    async forceUpdate() {
      if (this.$refs.accountSummary) {
        await this.$refs.accountSummary.updateAccountSummary();
      }
    },
    toggleDrawer() {
      this.isDrawerOpen = !this.isDrawerOpen;
    },
    handleDrawerClosed() {
      this.isDrawerOpen = false;
    },
    checkRoute() {
      const { name } = this.$route;
      const excludedRoutes = ['Login', 'Registration', 'ResetPassword', 'ErrorAuth', 'EmbedLogin', 'ChangePassword', 'VerifyEmail'];
      return name && !excludedRoutes.includes(name);
    },
    checkForAccountStatus(userInformation) {
      if (userInformation.accounts && userInformation.accounts.length) {
        const {
          status,
        } = userInformation.accounts[0];
        this.setAccountStatus(status);
      }
    },
    startIdleTimer() {
      this.$store.dispatch('idle/startIdle', {
        idleTime: getUserIdleTimeConfig(this.irAdmin),
      });
    },
    onIdle() {
      const isAutoLogoutView = this.checkRoute();
      if (isAutoLogoutView) {
        this.callLogout();
        this.setIdle();
      }
    },
    callLogout() {
      return this.$cognitoAuth.logout()
        .then(() => this.getStaticInformation())
        .then(() => {
          this.$router.push({ name: 'Login' });
        });
    },
    checkPhoneMfaSetup(user) {
      if (!user || !user.userId) {
        console.log('checkPhoneMfaSetup: No user or userId', { user, userId: user.userId });
        return;
      }

      // Don't show modal for admin users
      if (user.userType === 'ADMIN') {
        console.log('checkPhoneMfaSetup: Skipping admin user');
        return;
      }

      // Check if user needs to set up phone MFA
      const hasMfaPhonePreference = !!(user.mfaPhonePreference);
      const hasPhoneNumbers = !!(user.primaryPhoneNum || user.secondaryPhoneNum);

      console.log('checkPhoneMfaSetup:', {
        mfaPhonePreference: user.mfaPhonePreference,
        hasMfaPhonePreference,
        hasPhoneNumbers,
        primaryPhone: user.primaryPhoneNum,
        secondaryPhone: user.secondaryPhoneNum,
        shouldShow: !hasMfaPhonePreference && hasPhoneNumbers,
      });

      // Show dialog if user has phone numbers but no MFA phone preference is set
      if (!hasMfaPhonePreference && hasPhoneNumbers) {
        console.log('Showing phone preference dialog');
        this.currentUser = user;
        this.showPhonePreferenceDialog = true;
      }
    },
    async handlePhonePreferenceSaved() {
      // Refresh user profile to get updated data
      try {
        const updatedUser = await this.getUserProfile();
        this.setUser(updatedUser);
        this.user = updatedUser;
        this.currentUser = updatedUser;
        this.showPhonePreferenceDialog = false;
      } catch (error) {
        console.error('Error refreshing user profile:', error);
        this.showPhonePreferenceDialog = false;
      }
    },
    handlePhonePreferenceCancel() {
      // User cancelled, close dialog
      this.showPhonePreferenceDialog = false;
    },
  },
};
</script>

<style>
.bottom-margin {
  margin-bottom: 1%;
}

.text-16px {
  font-size: 16px;
}

.radio-item {
  display: inline-block;
  position: relative;
  padding: 0 6px;
}

.radio-item input[type='radio'] {
  display: none;
}

/* Mobile responsive overrides */
@media (max-width: 1024px) {
  .wrapper > section,
  .wrapper > footer {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .csstransforms3d .wrapper > section,
  .csstransforms3d .wrapper > footer {
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
  }

  .aside-toggled .wrapper > section,
  .aside-toggled .wrapper > footer {
    margin-left: 0 !important;
  }

  .csstransforms3d .aside-toggled .wrapper > section,
  .csstransforms3d .aside-toggled .wrapper > footer {
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
  }

  .content-wrapper {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
</style>



// WEBPACK FOOTER //
// App.vue?33ee78ec